import { BreakpointObserver } from "@angular/cdk/layout";
import { DOCUMENT } from "@angular/common";
import {
	Injectable,
	WritableSignal,
	effect,
	inject,
	signal,
} from "@angular/core";
import { persistentAtom } from "@nanostores/persistent";
import { BehaviorSubject, type Observable, map } from "rxjs";

const sidebarCollapsedAtom = persistentAtom<string>(
	"sidebarCollapsed",
	"false",
	{
		encode: JSON.stringify,
		decode: JSON.parse,
	},
);

@Injectable({
	providedIn: "root",
})
export class LayoutService {
	private readonly _breakpointObserver = inject(BreakpointObserver);
	private readonly _document = inject(DOCUMENT);

	public readonly mobileSidebarOpen: WritableSignal<boolean> = signal(false);
	public readonly sidebarCollapsed: WritableSignal<boolean> = signal(
		sidebarCollapsedAtom.get() === "true",
	);

	public isMobile$: Observable<boolean> = this._breakpointObserver
		.observe("(max-width: 1024px)")
		.pipe(map((result) => result.matches));

	public backButtonPresent$: BehaviorSubject<boolean> =
		new BehaviorSubject<boolean>(false);

	public readonly toolbarHeight: WritableSignal<number> = signal(0);

	constructor() {
		effect(() => {
			const toolbarHeight = this.toolbarHeight();
			this._onToolbarHeightChange(toolbarHeight);
		});
	}

	public toggleMobileSidebar(event?: Event): void {
		if (event) event.stopPropagation();
		this.mobileSidebarOpen.update((value) => !value);
	}

	public closeMobileSidebar(event?: Event): void {
		if (event) event.stopPropagation();
		this.mobileSidebarOpen.set(false);
	}

	public toggleSidebarCollapse(): void {
		this.sidebarCollapsed.update((value) => !value);
		// Persist the new state
		sidebarCollapsedAtom.set(this.sidebarCollapsed() ? "true" : "false");
	}

	private _onToolbarHeightChange(toolbarHeight: number): void {
		this._document.documentElement.style.setProperty(
			"--toolbarHeight",
			`${toolbarHeight}px`,
		);
	}
}
